import { useCallback } from "react";
import useUser from "../Core/hook/useUser";

export interface ModulePermission {
  can_view: boolean;
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
}

export type PermissionsMap = Record<string, ModulePermission>;

/**
 * usePermission — Lead/CRM permission facade.
 *
 * Bundled from DCC (localStorage-driven). Rewired to Step-Up's auth: it reads
 * the logged-in user via useUser() (backed by /user/userInfo) and maps:
 *   - isAdmin       → Step-Up Super Admin (System_Admin privilege) or admin role
 *   - hasPermission → admin OR the Step-Up privilege `${Module}_${Action}`
 *
 * 🟨 Step-Up has no dedicated "leads" privileges yet, so non-admins get no
 * lead access by default (admins get everything). Add Lead_View/Edit/... perms
 * and they'll flow through automatically. userId falls back to 0 when the
 * userInfo payload omits an id — "assigned to me" scoping needs a real id.
 */
const cap = (a: string) => a.charAt(0).toUpperCase() + a.slice(1);

export const usePermission = () => {
  const { userData, userHasRight } = useUser();

  const role = userData?.user_role_name ?? "";
  const userId = Number((userData as any)?.id ?? (userData as any)?.user_id ?? 0);

  const isAdmin = userHasRight("System_Admin") || /admin/i.test(role);
  const isSalesManager = /sales\s*manager/i.test(role);
  const canViewAllLeads = isAdmin || isSalesManager;
  const canViewFinancials = isAdmin;        // 🟨 widen to your finance roles
  const canViewCompanyDashboard = isAdmin;  // 🟨 widen if non-admins should see company totals

  const hasPermission = useCallback(
    (module: string, action: "view" | "create" | "edit" | "delete") =>
      isAdmin || userHasRight(`${module}_${cap(action)}`),
    [isAdmin, userHasRight]
  );

  const canView = useCallback((module: string) => hasPermission(module, "view"), [hasPermission]);
  const canCreate = useCallback((module: string) => hasPermission(module, "create"), [hasPermission]);
  const canEdit = useCallback((module: string) => hasPermission(module, "edit"), [hasPermission]);
  const canDelete = useCallback((module: string) => hasPermission(module, "delete"), [hasPermission]);

  return {
    permissions: {} as PermissionsMap,
    role,
    userId,
    isAdmin,
    isSalesManager,
    canViewAllLeads,
    canViewFinancials,
    canViewCompanyDashboard,
    hasPermission,
    canView,
    canCreate,
    canEdit,
    canDelete,
  };
};
